import React, { Component, ReactNode, ErrorInfo } from 'react';
import * as Sentry from '@sentry/browser';

/**
 * 子板块级错误边界。
 *
 * 背景：index-pdf 白皮书由 Promise.all 拉取多个数据接口后分发渲染，
 * 任一接口返回结构与前端期望错位（字段改名、层级变化、null），
 * 对应子组件渲染时会抛异常。React 17 下未捕获的异常会卸载整棵组件树，
 * 导致整页白屏。用本组件就近包裹每个子板块，将崩溃隔离在单个板块内，
 * 其余板块正常渲染，同时把错误上报到 console + Sentry 便于定位是哪个接口/模块。
 *
 * 仅负责渲染层容错，不改动任何数据 store 逻辑。
 */

interface IProps {
  // 板块名称，用于日志与 Sentry 标记，便于定位是哪个模块崩溃
  name: string;
  children: ReactNode;
  // 可选：自定义降级 UI，默认渲染低调占位，不破坏 PDF 排版
  fallback?: ReactNode;
}

interface IState {
  hasError: boolean;
}

class SectionBoundary extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): IState {
    // 渲染阶段捕获到异常，切换到降级 UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const { name } = this.props;
    // 打出模块名 + 错误栈，便于在控制台快速定位是哪个板块因数据问题崩溃
    // eslint-disable-next-line no-console
    console.error(`[SectionBoundary] 板块「${name}」渲染异常，已降级隔离：`, error, info?.componentStack);
    // 上报 Sentry，附带板块标记，便于线上排查具体接口/模块
    Sentry.withScope(scope => {
      scope.setTag('index_pdf_section', name);
      scope.setContext('sectionBoundary', { section: name, componentStack: info?.componentStack });
      Sentry.captureException(error);
    });
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { children, fallback, name } = this.props;

    if (hasError) {
      if (fallback !== undefined) return fallback;
      // 默认降级：低调占位，不破坏 PDF 整体排版
      return (
        <section
          data-section-error={name}
          style={{ minHeight: '1px', fontSize: 0, lineHeight: 0 }}/>
      );
    }

    return children;
  }
}

export default SectionBoundary;
